import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { PageShell } from "@/components/mai/PageShell";
import { useProduct } from "@/context/ProductContext";
import { toast } from "sonner";
import { Plus, PencilSimple, ArrowRight } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40);
const csv = (s) => String(s || "").split(",").map((x) => x.trim()).filter(Boolean);
const lines = (s) => String(s || "").split("\n").map((x) => x.trim()).filter(Boolean);

const Field = ({ label, children }) => (
  <div>
    <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">{label}</p>
    {children}
  </div>
);
const inputCls = "w-full text-xs border border-slate-300 px-2.5 py-2";

function WorkspaceForm({ initial, onClose, onSaved }) {
  const isEdit = !!initial;
  const [f, setF] = useState(() => ({
    name: initial?.product.name || "",
    company: initial?.product.company || "",
    category: initial?.product.category || "",
    indication: initial?.product.indication || "",
    target_population: initial?.product.target_population || "",
    development_stage: initial?.workspace?.development_stage || "",
    target_markets: (initial?.workspace?.target_markets || []).join(", "),
    existing_submissions: (initial?.workspace?.existing_submissions || []).join(", "),
    strategic_objectives: (initial?.workspace?.strategic_objectives || []).join("\n"),
  }));
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    if (!f.name.trim()) return toast.error("Product name is required");
    try {
      if (isEdit) {
        await axios.patch(`${API}/admin/products/${initial.product.id}`, {
          name: f.name, company: f.company, category: f.category,
          indication: f.indication, target_population: f.target_population,
        });
        if (initial.workspace) {
          await axios.patch(`${API}/admin/workspaces/${initial.workspace.id}`, {
            development_stage: f.development_stage,
            target_markets: csv(f.target_markets),
            existing_submissions: csv(f.existing_submissions),
            strategic_objectives: lines(f.strategic_objectives),
          });
        }
        toast.success("Workspace updated");
      } else {
        const slug = slugify(f.name);
        const orgs = (await axios.get(`${API}/admin/organizations`)).data;
        const orgId = orgs[0]?.id;
        await axios.post(`${API}/admin/workspaces`, {
          id: `${slug}-ws`, organization_id: orgId, name: `${f.name} — Market Access Workspace`,
          description: f.indication, development_stage: f.development_stage,
          target_markets: csv(f.target_markets), existing_submissions: csv(f.existing_submissions),
          strategic_objectives: lines(f.strategic_objectives),
        });
        const r = await axios.post(`${API}/admin/products`, {
          id: slug, name: f.name, organization_id: orgId, workspace_id: `${slug}-ws`,
          company: f.company, category: f.category, indication: f.indication,
          target_population: f.target_population,
        });
        toast.success(`Workspace created — ${r.data.markets_initialized} markets initialized`);
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="workspace-form-dialog" className="max-w-xl">
        <DialogHeader><DialogTitle className="font-heading text-sm">{isEdit ? "Edit Workspace" : "New Product Workspace"}</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Product name *"><input data-testid="ws-name-input" className={inputCls} value={f.name} onChange={set("name")} disabled={isEdit} placeholder="e.g. MyStent Pro" /></Field>
            <Field label="Company"><input className={inputCls} value={f.company} onChange={set("company")} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Category"><input className={inputCls} value={f.category} onChange={set("category")} placeholder="e.g. MedTech / Cardiology" /></Field>
            <Field label="Development stage"><input className={inputCls} value={f.development_stage} onChange={set("development_stage")} placeholder="e.g. Pivotal trial ongoing" /></Field>
          </div>
          <Field label="Indication"><input data-testid="ws-indication-input" className={inputCls} value={f.indication} onChange={set("indication")} /></Field>
          <Field label="Target population"><input className={inputCls} value={f.target_population} onChange={set("target_population")} /></Field>
          <Field label="Target markets (comma-separated)"><input data-testid="ws-markets-input" className={inputCls} value={f.target_markets} onChange={set("target_markets")} placeholder="Japan, Australia, Germany" /></Field>
          <Field label="Existing submissions (comma-separated)"><input className={inputCls} value={f.existing_submissions} onChange={set("existing_submissions")} placeholder="India (approved), EU CE mark" /></Field>
          <Field label="Strategic objectives (one per line)">
            <textarea data-testid="ws-objectives-input" className={`${inputCls} min-h-[80px]`} value={f.strategic_objectives} onChange={set("strategic_objectives")} placeholder={"Secure reimbursement in 3 formal-HTA markets by 2027\nEstablish APAC RWE program"} />
          </Field>
        </div>
        <button data-testid="ws-save-btn" onClick={save} className="w-full bg-slate-900 text-white text-xs font-semibold py-2.5 hover:bg-slate-800">
          {isEdit ? "Save Changes" : "Create Workspace"}
        </button>
      </DialogContent>
    </Dialog>
  );
}

export default function WorkspacePage() {
  const { products, productId, setProductId, refresh } = useProduct();
  const navigate = useNavigate();
  const [workspaces, setWorkspaces] = useState([]);
  const [form, setForm] = useState(null);

  const load = useCallback(() => axios.get(`${API}/admin/workspaces`).then((r) => setWorkspaces(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const onSaved = () => { setForm(null); load(); refresh(); };

  return (
    <PageShell pageId="workspaces" overline="Market Access Readiness Workspace · Layer 1" title="Product Workspaces"
      actions={
        <button data-testid="new-workspace-btn" onClick={() => setForm({ create: true })}
          className="bg-slate-900 text-white text-xs font-semibold px-4 py-2.5 hover:bg-slate-800 flex items-center gap-2">
          <Plus size={14} weight="bold" /> New Product Workspace
        </button>
      }>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {products.map((p) => {
          const ws = workspaces.find((w) => w.id === p.workspace_id);
          const isActive = p.id === productId;
          return (
            <div key={p.id} data-testid={`workspace-card-${p.id}`} className={`bg-white border ${isActive ? "border-slate-900" : "border-slate-200"}`}>
              <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-3">
                <div>
                  <p className="font-heading font-bold text-sm tracking-tight">{p.name}
                    {isActive && <span data-testid={`workspace-active-badge-${p.id}`} className="ml-2 text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 bg-emerald-700 text-white align-middle">● Active</span>}
                  </p>
                  <p className="text-[11px] text-slate-500 mt-0.5">{p.company}{p.category ? ` · ${p.category}` : ""}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button data-testid={`workspace-edit-${p.id}`} onClick={() => setForm({ product: p, workspace: ws })} title="Edit"
                    className="text-slate-400 hover:text-slate-900 border border-slate-200 p-1.5"><PencilSimple size={14} /></button>
                  <button data-testid={`workspace-open-${p.id}`}
                    onClick={() => {
                      if (!isActive) {
                        setProductId(p.id);
                        toast.success(`${p.name} is now the active product — opening its readiness analysis`);
                      }
                      navigate("/console");
                    }}
                    className="text-xs font-semibold text-white bg-slate-900 px-3 py-1.5 hover:bg-slate-800 flex items-center gap-1.5">
                    {isActive ? "Open Analysis" : "Analyze this Product"} <ArrowRight size={12} weight="bold" />
                  </button>
                </div>
              </div>
              <div className="px-5 py-4 space-y-3">
                <div>
                  <p className="overline-label text-slate-400">Indication</p>
                  <p className="text-xs text-slate-700 mt-0.5">{p.indication || "—"}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="overline-label text-slate-400">Target Markets</p>
                    <p className="text-xs text-slate-700 mt-0.5">{ws?.target_markets?.length ? ws.target_markets.join(", ") : "All platform markets"}</p>
                  </div>
                  <div>
                    <p className="overline-label text-slate-400">Existing Submissions</p>
                    <p className="text-xs text-slate-700 mt-0.5">{ws?.existing_submissions?.length ? ws.existing_submissions.join(", ") : "—"}</p>
                  </div>
                </div>
                <div>
                  <p className="overline-label text-slate-400">Strategic Objectives</p>
                  {ws?.strategic_objectives?.length ? (
                    <ul className="mt-1 space-y-1">
                      {ws.strategic_objectives.map((o, i) => (
                        <li key={i} className="text-xs text-slate-700 flex gap-2"><span className="text-slate-300">›</span>{o}</li>
                      ))}
                    </ul>
                  ) : <p className="text-xs text-slate-400 mt-0.5">Not yet defined — edit this workspace to add objectives.</p>}
                </div>
                {ws?.development_stage && (
                  <p className="text-[10px] text-slate-400 font-mono-data">Stage: {ws.development_stage}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {form && <WorkspaceForm initial={form.create ? null : form} onClose={() => setForm(null)} onSaved={onSaved} />}
    </PageShell>
  );
}
